'use client';

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NextIntlClientProvider } from 'next-intl';
import { useConvexAuth, useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import {
  type Locale,
  defaultLocale,
  isSupportedLocale,
  matchLocale,
  locales,
} from '@/i18n/config';
import { parsePartnerSubdomain, parseCustomDomainHost } from '@/lib/subdomain';

import { messages } from '@/i18n/messages';

/** Host-only cookie name used to server-render <html lang> on first paint
 * (read in src/app/layout.tsx via next/headers cookies()) and by
 * ClerkLocalizationProvider (which sits above this provider). */
const PARTNER_LOCALE_COOKIE = 'partner_locale';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// Helpers — all run at module level to avoid SSR issues
// ---------------------------------------------------------------------------

function readStoredLocale(): Locale | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem('locale');
    if (stored && isSupportedLocale(stored)) return stored;
  } catch {
    // localStorage may be unavailable in some environments
  }
  return null;
}

/** Reads the `partner_locale` cookie set by this provider once the partner
 * branding query resolves. Validates against the supported-locale list. */
function readPartnerLocaleCookie(): Locale | null {
  if (typeof document === 'undefined') return null;
  try {
    const match = document.cookie.match(/(?:^|;\s*)partner_locale=([^;]+)/);
    const value = match ? decodeURIComponent(match[1]) : null;
    if (value && isSupportedLocale(value)) return value;
  } catch {
    // document.cookie may be unavailable in some environments
  }
  return null;
}

/** Host-only — no `Domain` attribute — so a partner default set while
 * visiting one partner's subdomain (acme.42a.ai) never bleeds onto another
 * partner's subdomain (beta.42a.ai) in the same browser. A custom domain is
 * already a different origin and can't leak regardless. */
function writePartnerLocaleCookie(locale: Locale) {
  if (typeof document === 'undefined') return;
  try {
    document.cookie = `${PARTNER_LOCALE_COOKIE}=${encodeURIComponent(locale)}; path=/; max-age=31536000; SameSite=Lax`;
  } catch {
    // ignore
  }
}

function readBrowserLocale(): Locale | null {
  if (typeof window === 'undefined') return null;
  try {
    for (const tag of navigator.languages ?? [navigator.language]) {
      const matched = matchLocale(tag);
      if (matched) return matched;
    }
  } catch {
    // navigator.language may be unavailable in some environments
  }
  return null;
}

function resolveInitialLocale(): Locale {
  return readStoredLocale() ?? readPartnerLocaleCookie() ?? readBrowserLocale() ?? defaultLocale;
}

function applyLocale(locale: Locale) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
  try {
    localStorage.setItem('locale', locale);
  } catch {
    // ignore
  }
  // Notify listeners that live ABOVE LocaleProvider in the tree (e.g. the
  // ClerkLocalizationProvider) so Clerk's own UI can switch language too.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('localechange', { detail: locale }));
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  // Initialize from localStorage so first render matches the lang the inline
  // boot script already applied — no flash.
  const [locale, setLocaleState] = useState<Locale>(resolveInitialLocale);

  // Fire-and-forget Convex mutation to persist the preference server-side.
  const setLocaleMutation = useMutation(api.users.setLocale);

  // Gate on Convex auth (not Clerk) to avoid unauthenticated query errors on
  // sign-out. Mirrors the pattern used in useOrganizationPermissions.tsx.
  const { isAuthenticated } = useConvexAuth();
  const currentUser = useQuery(
    api.users.getCurrentUser,
    isAuthenticated ? {} : 'skip',
  );

  // Track whether we've already adopted the server locale so we only do it
  // once per session (don't fight the user if they switch after load).
  const serverReconciled = useRef(false);

  useEffect(() => {
    if (serverReconciled.current) return;
    if (currentUser === undefined) return; // still loading

    const serverLocale = currentUser?.locale;
    if (serverLocale && isSupportedLocale(serverLocale)) {
      const storedLocale = readStoredLocale();
      // Server is source of truth on a fresh device (no localStorage entry).
      if (!storedLocale || storedLocale !== serverLocale) {
        setLocaleState(serverLocale);
        applyLocale(serverLocale);
      }
    }
    serverReconciled.current = true;
  }, [currentUser]);

  // Resolve the current host's partner (subdomain or custom-domain) so a
  // first-time visitor on a branded host sees that partner's default
  // language. Mirrors usePartnerBranding's host-resolution pair, but as a
  // light dedicated query — only `defaultLocale` is needed here.
  const subdomain = useMemo(
    () => (typeof window !== 'undefined' ? parsePartnerSubdomain(window.location.hostname) : null),
    [],
  );
  const subdomainPartner = useQuery(
    api.partners.getPartnerBrandingBySubdomain,
    subdomain ? { subdomain } : 'skip',
  );
  const customDomainHost = useMemo(
    () => (typeof window !== 'undefined' && !subdomain ? parseCustomDomainHost(window.location.host) : null),
    [subdomain],
  );
  const customDomainPartner = useQuery(
    api.partners.getPartnerBrandingByCustomDomain,
    customDomainHost ? { host: customDomainHost } : 'skip',
  );
  const partnerDefaultLocale = subdomainPartner?.defaultLocale ?? customDomainPartner?.defaultLocale ?? null;

  // Track whether we've already adopted a partner default this session, so a
  // user who explicitly switches language afterwards isn't fought on
  // re-render (mirrors serverReconciled above).
  const partnerLocaleAdopted = useRef(false);

  useEffect(() => {
    if (partnerLocaleAdopted.current) return;
    if (!partnerDefaultLocale || !isSupportedLocale(partnerDefaultLocale)) return;

    // Precedence rule 1 always wins: never overwrite an explicit user choice
    // (stored localStorage pick, or an already-adopted server users.locale).
    if (readStoredLocale()) {
      partnerLocaleAdopted.current = true;
      return;
    }

    setLocaleState(partnerDefaultLocale);
    applyLocale(partnerDefaultLocale);
    // Write the cookie regardless of whether we changed state — it's what
    // layout.tsx reads server-side for the next request's first paint, and
    // what ClerkLocalizationProvider reads on mount above this provider.
    writePartnerLocaleCookie(partnerDefaultLocale);
    partnerLocaleAdopted.current = true;
  }, [partnerDefaultLocale]);

  const setLocale = (next: Locale) => {
    setLocaleState(next);
    applyLocale(next);
    // Fire-and-forget — ignore errors (user may not be signed in)
    if (isAuthenticated) {
      setLocaleMutation({ locale: next }).catch(() => {
        // best-effort; localStorage already updated
      });
    }
  };

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      <NextIntlClientProvider
        locale={locale}
        messages={messages[locale]}
        // Suppress missing-translation warnings in the console during Phase 0
        // when most strings are not yet extracted. Remove once Phase 1 lands.
        onError={() => undefined}
      >
        {children}
      </NextIntlClientProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
}

// Expose supported locales list for switcher components
export { locales };
export type { Locale };
